
import { CastApi } from "@/adapter/castApi";
import { storage } from "@/plugins/firebase";
import draggable from "vuedraggable";
import firebase from "firebase/app";
import { getYYYYMMDDhhmmss } from "@/helper/utils/dateUtils";
import Vue from "vue";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

interface IImgSrcUrlList {
  [imgSrc: string]: string;
}
interface ISelectedImg {
  imgSrc: string;
  imgUrl: string;
}
interface IImgInfo {
  path: string;
  event: any;
}

export default Vue.extend({
  components: {
    draggable,
  },
  data(): {
    dialog: boolean;
    imgSrcList: string[];
    errMsg: string;
    imgSrcUrlList: IImgSrcUrlList;
    selectedImgUrl: ISelectedImg;
    showAvatar: boolean;
    addImgInfo: IImgInfo[];
  } {
    return {
      dialog: false,
      imgSrcList: [],
      errMsg: "",
      imgSrcUrlList: {},
      selectedImgUrl: {
        imgSrc: "",
        imgUrl: "",
      },
      showAvatar: false,
      addImgInfo: [],
    };
  },
  methods: {
    async openDialog(imgSrcList: string[]) {
      // 値渡しにするため
      this.imgSrcList = [...imgSrcList];

      this.dialog = true;
      await this.initImg();
      this.showAvatar = true;
    },
    closeDialog() {
      this.showAvatar = false;
      this.dialog = false;
    },
    async initImg() {
      if (this.imgSrcList.length == 0) {
        this._clearImgSrcUrlList();
        this._clearSelectedImg();
        return;
      }
      await this.setImgSrcUrlList(this.imgSrcList);
      await this.setSelectedImg(this.imgSrcList[0]);
    },
    onClickAddImgBtn() {
      (this.$refs as any).imgInput.click();
    },
    async onFileDrop() {
      const event = (this.$refs as any).imgInput.files[0];
      this.errMsg = "";
      if (!event) {
        return;
      }

      if (event.size > 2000000) {
        this.errMsg = "画像のサイズが制限(2MB)を超えています。";
        return;
      }

      // ファイル名
      const fileName = getYYYYMMDDhhmmss() + "_" + event.name;
      const uid = this.$store.getters["auth/getUid"];
      const filePath = "mullion/casts/imgSrc/" + uid + "/" + fileName;

      this.addImgInfo.push({
        path: filePath,
        event: event,
      });

      // 画像を追加
      this.imgSrcUrlList[filePath] = URL.createObjectURL(event);
      this.imgSrcList.push(filePath);
      if (this.imgSrcList.length == 1) {
        await this.setSelectedImg(this.imgSrcList[0]);
      }
    },
    async saveCastImgData() {
      await this.$store.dispatch("startLoading");
      // 画像をstorageにアップロード
      if (this.addImgInfo.length > 0) {
        for (const imgInfo of this.addImgInfo) {
          await this.putImgToStorage(imgInfo.path, imgInfo.event);
        }
      }

      // firestoreのデータを更新
      const casts = new CastApi();
      await casts.setCastData({
        imgSrcList: this.imgSrcList,
      });

      // 親コンポーネントに通知
      await this.$emit("uploadedImg", this.imgSrcList);
      this.closeDialog();
      await this.$store.dispatch("finishLoading");
    },
    async putImgToStorage(filePath: string, event: any) {
      const reference = ref(storage, filePath);
      await uploadBytesResumable(reference, event).catch(async (error) => {
        this.errMsg =
          "画像のアップロード時にエラーが発生しました。お手数ですが、再度お試しください。";
        // todo
        if (error.code == "storage/unauthorized") {
          return;
        } else if (error.code == "storage/canceled") {
          return;
        } else if (error.code == "storage/unknown") {
          return;
        }
        await this.finishLoading();
      });
    },
    async finishLoading() {
      await this.$store.dispatch("finishLoading");
    },
    async getImgSrcUrl(imgSrc: string): Promise<string> {
      return await getDownloadURL(ref(storage, imgSrc))
        .then((url: string) => {
          return url;
        })
        .catch((err) => {
          console.log(err);
          return "";
        });
    },
    async setSelectedImg(imgSrc: string) {
      const url = this.imgSrcUrlList[imgSrc];
      if (this.selectedImgUrl.imgUrl != url) {
        this._setSelectedImgUrl(imgSrc, url);
      }
    },
    async setImgSrcUrlList(imgSrcList: string[]) {
      await Promise.all(
        imgSrcList.map(async (imgSrc: string) => {
          const filter = this.addImgInfo.filter(
            (el: IImgInfo) => el.path == imgSrc
          );
          if (filter.length > 0) {
            this.imgSrcUrlList[filter[0].path] = URL.createObjectURL(
              filter[0].event
            );
            return;
          }
          const url = await this.getImgSrcUrl(imgSrc);
          this.imgSrcUrlList[imgSrc] = url;
        })
      );
    },
    _clearImgSrcUrlList() {
      this.imgSrcUrlList = {};
    },
    async deleteImg(selectedImg: ISelectedImg) {
      this.imgSrcList = this.imgSrcList.filter(
        (imgSrc) => imgSrc != selectedImg.imgSrc
      );
      this.addImgInfo = this.addImgInfo.filter(
        (imgInfo) => imgInfo.path != selectedImg.imgSrc
      );
      await this.initImg();
    },
    _setSelectedImgUrl(imgSrc: string, imgUrl: string) {
      this.selectedImgUrl = {
        imgSrc: imgSrc,
        imgUrl: imgUrl,
      };
    },
    _clearSelectedImg() {
      this.selectedImgUrl = {
        imgSrc: "",
        imgUrl: "",
      };
    },
  },
});
